import React from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import { useDocumentTitle } from '../hooks/core';

export const PrivacyPolicyPage: React.FC = () => {
  useDocumentTitle('Privacy Policy');
  const effectiveDate = 'January 28, 2026';

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: 1, borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Effective Date: {effectiveDate}
        </Typography>
        <Divider sx={{ my: 3 }} />

        {/* Introduction */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" paragraph>
            <strong>Katapult Media, Inc.</strong> ("Company," "we," "us," or "our") operates the SquareUp platform
            and related services (the "Service"). This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you use our Service.
          </Typography>
          <Typography variant="body1" paragraph>
            Please read this Privacy Policy carefully. By using SquareUp, you consent to the practices described
            in this policy. If you do not agree with this policy, please do not use the Service.
          </Typography>
        </Box>

        {/* Section 1: Information We Collect */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            1. Information We Collect
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Personal Information
          </Typography>
          <Typography variant="body1" paragraph>
            When you create an account or use our Service, we may collect:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1"><strong>Account Information:</strong> Display name, email address, and password (encrypted)</Typography></li>
            <li><Typography variant="body1"><strong>Profile Information:</strong> Handle (4-character display code) and selected avatar icon</Typography></li>
            <li><Typography variant="body1"><strong>Payment Information:</strong> When you purchase credits, our payment processor (Stripe) collects billing details. We do not store credit card numbers on our servers.</Typography></li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Usage Data
          </Typography>
          <Typography variant="body1" paragraph>
            We automatically collect certain information when you use the Service:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1"><strong>Device Information:</strong> Browser type, operating system, device type</Typography></li>
            <li><Typography variant="body1"><strong>Log Data:</strong> IP address, access times, pages viewed, referring URL</Typography></li>
            <li><Typography variant="body1"><strong>Usage Patterns:</strong> Features used, boards created/joined, interactions with the Service</Typography></li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Cookies and Tracking Technologies
          </Typography>
          <Typography variant="body1" paragraph>
            We use cookies and similar technologies to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Maintain your session and keep you logged in</Typography></li>
            <li><Typography variant="body1">Remember your preferences</Typography></li>
            <li><Typography variant="body1">Analyze usage patterns and improve the Service</Typography></li>
            <li><Typography variant="body1">Provide security features (CSRF protection)</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            You can control cookies through your browser settings, but disabling essential cookies may affect
            Service functionality.
          </Typography>
        </Box>

        {/* Section 2: How We Use Your Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            2. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use the information we collect to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1"><strong>Provide the Service:</strong> Create and manage your account, enable board participation, process transactions</Typography></li>
            <li><Typography variant="body1"><strong>Process Payments:</strong> Facilitate credit purchases through our payment processor</Typography></li>
            <li><Typography variant="body1"><strong>Communicate:</strong> Send transactional emails (account verification, password resets, purchase confirmations)</Typography></li>
            <li><Typography variant="body1"><strong>Improve the Service:</strong> Analyze usage patterns, fix bugs, develop new features</Typography></li>
            <li><Typography variant="body1"><strong>Security:</strong> Detect and prevent fraud, abuse, and security threats</Typography></li>
            <li><Typography variant="body1"><strong>Legal Compliance:</strong> Comply with applicable laws, regulations, and legal processes</Typography></li>
          </Box>
        </Box>

        {/* Section 3: How We Share Your Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            3. How We Share Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We may share your information in the following circumstances:
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Service Providers
          </Typography>
          <Typography variant="body1" paragraph>
            We share information with third-party service providers who assist in operating the Service:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1"><strong>Stripe:</strong> Payment processing (see Stripe's Privacy Policy)</Typography></li>
            <li><Typography variant="body1"><strong>Cloud Hosting:</strong> Data storage and infrastructure</Typography></li>
            <li><Typography variant="body1"><strong>Email Services:</strong> Transactional email delivery</Typography></li>
            <li><Typography variant="body1"><strong>Analytics:</strong> Usage analysis and improvement</Typography></li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Board Participants
          </Typography>
          <Typography variant="body1" paragraph>
            When you join a board, other participants can see your:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Display name</Typography></li>
            <li><Typography variant="body1">Handle (if set)</Typography></li>
            <li><Typography variant="body1">Selected avatar icon</Typography></li>
            <li><Typography variant="body1">Squares you own on that board</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            Your email address is not shared with other participants.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Legal Requirements
          </Typography>
          <Typography variant="body1" paragraph>
            We may disclose information if required by law, court order, or government request, or if we believe
            disclosure is necessary to protect our rights, your safety, or the safety of others.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Government & Legal Data Requests
          </Typography>
          <Typography variant="body1" paragraph>
            When we receive requests from government authorities or law enforcement for user data, we follow these principles:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1"><strong>Legal Review:</strong> All government data requests are reviewed for legal validity before any disclosure. We verify that requests comply with applicable laws and include proper legal process (such as valid subpoenas, court orders, or warrants).</Typography></li>
            <li><Typography variant="body1"><strong>Challenge Unlawful Requests:</strong> We may challenge requests that we believe are overly broad, legally deficient, or otherwise inappropriate. We push back on requests that do not meet legal requirements.</Typography></li>
            <li><Typography variant="body1"><strong>Data Minimization:</strong> When disclosure is legally required, we provide only the minimum information necessary to comply with the specific request. We do not provide more data than what is explicitly required.</Typography></li>
            <li><Typography variant="body1"><strong>Documentation:</strong> We maintain records of government data requests, our responses, and the legal basis for any disclosures made.</Typography></li>
            <li><Typography variant="body1"><strong>User Notification:</strong> Unless legally prohibited (such as by a court order or gag provision), we will attempt to notify affected users of government requests for their data so they may seek legal counsel if desired.</Typography></li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Business Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            In the event of a merger, acquisition, or sale of assets, your information may be transferred to
            the acquiring entity. We will notify you of any such change.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            With Your Consent
          </Typography>
          <Typography variant="body1" paragraph>
            We may share information for other purposes with your explicit consent.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Aggregated Data
          </Typography>
          <Typography variant="body1" paragraph>
            We may share aggregated, anonymized data that cannot identify you for analytics and research purposes.
          </Typography>
        </Box>

        {/* Section 4: Data Security */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            4. Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement appropriate technical and organizational measures to protect your information:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1"><strong>Encryption:</strong> Data transmitted to and from the Service is encrypted using TLS/SSL</Typography></li>
            <li><Typography variant="body1"><strong>Password Security:</strong> Passwords are hashed and never stored in plain text</Typography></li>
            <li><Typography variant="body1"><strong>Access Controls:</strong> Employee access to user data is limited and logged</Typography></li>
            <li><Typography variant="body1"><strong>Secure Infrastructure:</strong> We use reputable cloud providers with strong security practices</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            <strong>Breach Notification:</strong> In the event of a data breach affecting your personal information,
            we will notify you and relevant authorities as required by applicable law.
          </Typography>
          <Typography variant="body1" paragraph>
            While we strive to protect your information, no method of transmission or storage is 100% secure.
            We cannot guarantee absolute security.
          </Typography>
        </Box>

        {/* Section 5: Your Rights & Controls */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            5. Your Rights & Controls
          </Typography>
          <Typography variant="body1" paragraph>
            You have the following rights regarding your personal information:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1"><strong>Access:</strong> Request a copy of the personal data we hold about you</Typography></li>
            <li><Typography variant="body1"><strong>Correction:</strong> Update or correct inaccurate information in your profile settings</Typography></li>
            <li><Typography variant="body1"><strong>Deletion:</strong> Request deletion of your account and associated data</Typography></li>
            <li><Typography variant="body1"><strong>Data Portability:</strong> Request your data in a portable format</Typography></li>
            <li><Typography variant="body1"><strong>Marketing Opt-Out:</strong> Unsubscribe from marketing communications at any time</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            To exercise these rights, contact us at legal@katapultmedia.com. We will respond within 30 days.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Cookie Preferences
          </Typography>
          <Typography variant="body1" paragraph>
            You can manage cookie preferences through your browser settings. Note that disabling certain cookies
            may limit your ability to use some features of the Service.
          </Typography>
        </Box>

        {/* Section 6: Data Retention */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            6. Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain your information for as long as your account is active or as needed to provide the Service.
            Specifically:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1"><strong>Account Data:</strong> Retained until you delete your account</Typography></li>
            <li><Typography variant="body1"><strong>Transaction Records:</strong> Retained for 7 years for legal and tax compliance</Typography></li>
            <li><Typography variant="body1"><strong>Usage Logs:</strong> Retained for up to 12 months</Typography></li>
            <li><Typography variant="body1"><strong>Backup Data:</strong> May persist in backups for up to 90 days after deletion</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            After account deletion, we may retain certain information as required by law or for legitimate
            business purposes (such as resolving disputes or enforcing our agreements).
          </Typography>
        </Box>

        {/* Section 7: Children's Privacy */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            7. Children's Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            SquareUp is not intended for children under the age of 18. We do not knowingly collect personal
            information from children under 18. If you are under 18, please do not use the Service or provide
            any personal information.
          </Typography>
          <Typography variant="body1" paragraph>
            If we learn that we have collected personal information from a child under 18, we will take steps
            to delete that information as soon as possible. If you believe we may have collected information
            from a child under 18, please contact us at legal@katapultmedia.com.
          </Typography>
        </Box>

        {/* Section 8: International Users */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            8. International Users
          </Typography>
          <Typography variant="body1" paragraph>
            SquareUp is operated from the United States. If you access the Service from outside the United States,
            please be aware that your information may be transferred to, stored, and processed in the United States,
            where our servers are located.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            European Users (GDPR)
          </Typography>
          <Typography variant="body1" paragraph>
            If you are located in the European Economic Area (EEA), you have additional rights under the General
            Data Protection Regulation (GDPR):
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Right to access, rectify, or erase your personal data</Typography></li>
            <li><Typography variant="body1">Right to restrict or object to processing</Typography></li>
            <li><Typography variant="body1">Right to data portability</Typography></li>
            <li><Typography variant="body1">Right to withdraw consent at any time</Typography></li>
            <li><Typography variant="body1">Right to lodge a complaint with a supervisory authority</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            Our legal basis for processing your data includes: performance of our contract with you, your consent,
            and our legitimate business interests.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            California Users (CCPA)
          </Typography>
          <Typography variant="body1" paragraph>
            If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA):
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Right to know what personal information we collect and how it's used</Typography></li>
            <li><Typography variant="body1">Right to delete your personal information</Typography></li>
            <li><Typography variant="body1">Right to opt-out of the sale of personal information (we do not sell your data)</Typography></li>
            <li><Typography variant="body1">Right to non-discrimination for exercising your rights</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            To exercise your CCPA rights, contact us at legal@katapultmedia.com.
          </Typography>
        </Box>

        {/* Section 9: Third-Party Services */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            9. Third-Party Services
          </Typography>
          <Typography variant="body1" paragraph>
            The Service may contain links to third-party websites or use third-party services. This Privacy Policy
            does not apply to those third parties. We encourage you to review their privacy policies:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1"><strong>Stripe:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            We are not responsible for the privacy practices of third-party services.
          </Typography>
        </Box>

        {/* Section 10: Policy Updates */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            10. Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. When we make material changes, we will:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Update the "Effective Date" at the top of this policy</Typography></li>
            <li><Typography variant="body1">Post the updated policy on the Service</Typography></li>
            <li><Typography variant="body1">Notify you via email or in-app notification for significant changes</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            We encourage you to review this Privacy Policy periodically. Your continued use of the Service after
            changes are posted constitutes acceptance of the updated policy.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            11. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about this Privacy Policy or our privacy practices, please contact us:
          </Typography>
          <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'primary.main' }}>
            <Typography variant="body1">
              <strong>Katapult Media, Inc.</strong>
            </Typography>
            <Typography variant="body1">
              Privacy & Legal Inquiries: legal@katapultmedia.com
            </Typography>
            <Typography variant="body1">
              General Support: support@katapultmedia.com
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary" align="center">
          By using SquareUp, you acknowledge that you have read and understand this Privacy Policy.
        </Typography>
      </Paper>
    </Box>
  );
};

export default PrivacyPolicyPage;
